// Cliente HTTP minimal hasta que se corra `pnpm api:gen`.
// Después de api:gen, src/lib/api/gen/ tendrá funciones tipadas que reemplazan estas.

import { toast } from "sonner";

const API_BASE = "/api";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/** Item de error de validación de FastAPI (422). */
export type ValidationItem = {
  type: string;
  loc: (string | number)[];
  msg: string;
  ctx?: Record<string, unknown>;
};

/** Traduce un item de validación de FastAPI a un mensaje legible en español. */
export function translateValidationItem(item: ValidationItem): string {
  const ctx = item.ctx ?? {};
  switch (item.type) {
    case "string_too_long":
      return `Máximo ${ctx.max_length} caracteres`;
    case "string_too_short":
      return `Mínimo ${ctx.min_length} caracteres`;
    case "string_pattern_mismatch":
      return "Formato inválido";
    case "missing":
      return "Campo requerido";
    case "value_error":
      return item.msg.includes("email") ? "Email inválido" : "Valor inválido";
    case "greater_than_equal":
      return `Debe ser ≥ ${ctx.ge}`;
    case "less_than_equal":
      return `Debe ser ≤ ${ctx.le}`;
    default:
      return item.msg;
  }
}

/** Construye un mensaje legible a partir del array `detail` de un 422. */
function formatValidationDetail(detail: unknown): string {
  if (Array.isArray(detail)) {
    const parts = (detail as ValidationItem[]).map((it) => {
      const field = it.loc?.[it.loc.length - 1];
      return field ? `${field}: ${translateValidationItem(it)}` : translateValidationItem(it);
    });
    if (parts.length) return parts.join(" · ");
  }
  return "Error de API";
}

type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined | null>;
};

export async function request<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, headers = {}, query } = options;
  let url = `${API_BASE}${path}`;
  if (query) {
    const qs = new URLSearchParams(
      Object.entries(query)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => [k, String(v)]),
    ).toString();
    if (qs) url += `?${qs}`;
  }
  const res = await fetch(url, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...headers },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return null as T;
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    const detail = (data && (data as { detail?: unknown }).detail) || `HTTP ${res.status}`;
    const msg = typeof detail === "string" ? detail : formatValidationDetail(detail);
    if (res.status !== 401) toast.error(msg);
    throw new ApiError(res.status, msg, data);
  }
  return data as T;
}
